import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories, domains } from '@/mocks/domains';

const collectionPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: '狼友域名分类导航',
  description: '按类别浏览狼友域名，包括推荐域名、主域名、镜像域名、备用入口、防失联域名和资源站点。',
  url: 'https://langyouyuming.com/category/',
};

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'recommended');

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeCategory) || categories[0];
  const filteredDomains = domains.filter((d) => d.category === activeCategory);
  const onlineDomains = filteredDomains.filter((d) => d.status === 'online');
  const offlineDomains = filteredDomains.filter((d) => d.status === 'offline');

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(collectionPageSchema)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-24 pb-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            {/* Breadcrumb */}
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: '首页', to: '/' },
                  { label: '域名分类' },
                  { label: currentCategory.name },
                ]}
              />
            </div>

            {/* Page Header */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 flex items-center justify-center bg-accent-100 rounded-xl">
                  <i className={`${currentCategory.icon} text-accent-600 text-lg`}></i>
                </div>
                <div>
                  <h1 className="font-heading text-2xl md:text-3xl text-foreground-950">
                    {currentCategory.name}
                  </h1>
                </div>
              </div>
              <p className="text-sm text-foreground-500 ml-13 pl-0 md:pl-13">
                {currentCategory.description}
              </p>
            </div>

            {/* Category Tabs */}
            <div className="flex flex-wrap gap-2 mb-8">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-900'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            {/* Stats */}
            <div className="flex flex-wrap items-center gap-4 mb-6 text-sm">
              <span className="text-foreground-600">
                共 <strong className="text-foreground-950">{filteredDomains.length}</strong> 个域名
              </span>
              <span className="flex items-center gap-1.5 text-accent-600">
                <span className="w-2 h-2 bg-accent-500 rounded-full"></span>
                <strong>{onlineDomains.length}</strong> 个在线
              </span>
              {offlineDomains.length > 0 && (
                <span className="flex items-center gap-1.5 text-foreground-400">
                  <span className="w-2 h-2 bg-foreground-300 rounded-full"></span>
                  <strong>{offlineDomains.length}</strong> 个离线
                </span>
              )}
            </div>

            {/* Domain List */}
            <div className="space-y-3">
              {/* Online first */}
              {onlineDomains.map((domain) => (
                <a
                  key={domain.id}
                  href={domain.url}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="group flex flex-col sm:flex-row sm:items-center gap-4 bg-background-50 rounded-xl p-5 border border-background-200/70 hover:border-accent-300 transition-all duration-300 cursor-pointer"
                >
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="w-2.5 h-2.5 bg-accent-500 rounded-full"></span>
                    <span className="text-xs text-accent-600 font-medium whitespace-nowrap">在线</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading text-base text-foreground-950 mb-1 group-hover:text-primary-600 transition-colors">
                      {domain.name}
                    </h3>
                    <p className="text-sm text-foreground-500 line-clamp-1">{domain.description}</p>
                  </div>
                  <div className="flex items-center gap-4 flex-shrink-0">
                    <div className="text-right">
                      <div className="text-xs text-foreground-500">速度</div>
                      <div className="text-sm font-medium text-accent-600">{domain.speed}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-foreground-500">在线率</div>
                      <div className="text-sm font-medium text-foreground-700">{domain.uptime}</div>
                    </div>
                    <div className="flex items-center gap-1 text-accent-600 text-sm font-medium whitespace-nowrap">
                      <i className="ri-external-link-line"></i>
                      访问
                    </div>
                  </div>
                </a>
              ))}

              {/* Offline second */}
              {offlineDomains.map((domain) => (
                <div
                  key={domain.id}
                  className="flex flex-col sm:flex-row sm:items-center gap-4 bg-background-50 rounded-xl p-5 border border-background-200/70 opacity-60"
                >
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="w-2.5 h-2.5 bg-foreground-300 rounded-full"></span>
                    <span className="text-xs text-foreground-400 font-medium whitespace-nowrap">离线</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading text-base text-foreground-500 mb-1">{domain.name}</h3>
                    <p className="text-sm text-foreground-400 line-clamp-1">{domain.description}</p>
                  </div>
                  <div className="flex items-center gap-4 flex-shrink-0">
                    <div className="text-right">
                      <div className="text-xs text-foreground-400">速度</div>
                      <div className="text-sm font-medium text-foreground-400">{domain.speed}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-foreground-400">在线率</div>
                      <div className="text-sm font-medium text-foreground-400">{domain.uptime}</div>
                    </div>
                    <span className="text-xs text-foreground-400 whitespace-nowrap">维护中</span>
                  </div>
                </div>
              ))}

              {filteredDomains.length === 0 && (
                <div className="text-center py-16">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-background-100 rounded-2xl">
                    <i className="ri-inbox-line text-2xl text-foreground-300"></i>
                  </div>
                  <p className="text-foreground-500">该分类暂无域名</p>
                </div>
              )}
            </div>

            {/* Bottom CTA */}
            <div className="mt-12 p-6 bg-accent-100/50 rounded-xl border border-accent-200/50 text-center">
              <p className="text-sm text-accent-700 mb-3">
                没有找到可用的域名？试试我们的防失联永久入口
              </p>
              <a
                href="https://langyou-permanent.com"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-accent-500 text-white rounded-full text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-shield-check-line"></i>
                访问防失联永久入口
              </a>
            </div>
          </div>
        </main>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}